<template>
  <div class="general-settings single-step" v-if="program">
    <div class="heading">
      <h2>{{ $ml.get(`General Settings`) }}</h2>
      <app-btn
        v-if="!isFinished && isNextStepAvailable"
        :loading="loading"
        @click="save"
        :disabled="$store.getters['user/plan'] == 'impact'">
        {{ $ml.get(`refresh`) }}
      </app-btn>
    </div>
    <div class="a-rows">

      <div class="a-row">
        <div class="a-column">
          <app-text-input
            :label="$ml.get(`Program Name`)"
            v-model="form.name"
          />
        </div>
        <div class="a-column">
          <app-sel
            v-model="form.farmId"
            :items="farms"
            key-name="id"
            label-name="name"
            :label="$ml.get(`Select farm`)"
          >
            <template v-slot="{ items }">
              <app-sel-opt
                v-for="item in items"
                :value="item"
                :key="item.id"
              >
                {{ item.name }}
              </app-sel-opt>
            </template>
          </app-sel>
        </div>
        <div class="a-column">
          <app-sel
            v-model="form.fieldId"
            :items="fields"
            key-name="id"
            label-name="name"
            :label="$ml.get(`Select field`)"
            :disabled="!form.farmId"
          >
            <template v-slot="{ items }">
              <app-sel-opt
                v-for="item in items"
                :value="item"
                :key="item.id"
              >
                {{ item.name }}
              </app-sel-opt>
            </template>
          </app-sel>
        </div>

        <div class="a-column">
          <date-selector v-model="form.plantingDate" />
        </div>
        <template v-if="$store.getters['user/plan'] != 'impact'">
          <div class="a-column" v-if="program.type == 'soil-based'">
            <app-sel
              v-model="form.tillageId"
              :label="$ml.get(`Tillage`)"
              :items="tillages"
              key-name="id"
              label-name="name"
              :info-tip="$ml.get(`Different tillage practices affect runoff, leaching, soil compaction and organic matter`)"
            >
              <template v-slot="{ items }">
                <app-sel-opt v-for="item in items" :value="item" :key="item.id" >
                  {{ $ml.get(item.name) }}
                </app-sel-opt>
              </template>
            </app-sel>
          </div>
          <div class="a-column" v-if="program.type == 'soil-based'">
            <app-sel
              v-model="form.previousCropId"
              :items="previousCrops"
              key-name="id"
              label-name="name"
              :label="$ml.get(`Previous crop`)"
            >
              <template v-slot="{ items }">
                <app-sel-opt
                  v-for="item in items"
                  :value="item"
                  :key="item.id"
                  v-if="form.soilTest == item.soilTest"
                >
                  {{ $ml.get(item.name) }}
                </app-sel-opt>
              </template>
            </app-sel>
          </div>

          <div class="a-column" v-if="$store.getters['user/plan'] != 'impact'">
            <fertigation-selector v-model="form.fertigation" />
          </div>
        </template>


      </div>

      <div class="a-row">
        <div class="a-column check" v-if="program.type == 'soil-based'">
          <app-checkbox :label="$ml.get(`Soil test`)" v-model="form.soilTest" :info-tip="$ml.get(`Thick the box if you have a soil test`)" />
        </div>
        <div class="a-column check">
          <app-checkbox :label="$ml.get(`Tissue test`)" v-model="form.tissueTest" :info-tip="$ml.get(`Tick the box have a leaf or other tissue test`)" />
        </div>

        <div class="a-column check">
          <app-checkbox :label="$ml.get(`Water test`)" v-model="form.waterTest" :info-tip="$ml.get(`Tick the box if have a water test`)" />
        </div>

        <div v-if="this.program.type == 'hydroponic'" class="a-column">
          <app-checkbox :label="$ml.get(`Media analysis`)" v-model="form.mediaAnalysis" :info-tip="$ml.get(`Tick the box if have a media analysis`)" />
        </div>

        <template v-if="$store.getters['user/plan'] != 'impact'">


          <div class="a-column check" v-if="program.type == 'soil-based' && $store.getters['user/canDoLimig']">
            <app-checkbox :label="$ml.get(`Liming`)" v-model="form.liming" :info-tip="$ml.get(`Tick the box if you want to use this feature to calculate the amount of liming material needed to raise your soil pH`)" />
          </div>

          <div class="a-column check" v-if="program.type == 'soil-based'">
            <app-checkbox :label="$ml.get(`Base Dressing`)" v-model="form.baseDressing" :info-tip="$ml.get(`Would you like to fertilize your soil before sowing/planting?`)" />
          </div>

          <div class="a-column check" v-if="program.type == 'soil-based'">
            <app-checkbox :label="$ml.get(`Need a blend?`)" v-model="form.blend" :info-tip="$ml.get(`Would you like to prepare a mix of fertilizers ready to pour in your spreader?`)" />
          </div>

          <div class="a-column check">
            <app-checkbox
              :label="$ml.get(`GDD`)"
              v-model="form.gdd"
              v-if="$whitelabel !== 'digifarm' && $store.getters['user/data'].globalRole == 'admin'"
              :info-tip="$ml.get(`Growing Degrees Days - The length of the growth stages will be re-calculated based on the local historical temperatures. Increase accuracy of the Fertilization Plan`)"
            />
          </div>

        </template>


      </div>

    </div>

    <div class="heading">
      <h2>{{ $ml.get(`Crop Selection`) }}</h2>
    </div>

    <crop-selection/>
  </div>
</template>

<script>
import API from '@/api'
import { ProgramAPI } from '@/api'
import CropSelection from './CropSelection'
import { ErrorHandlerMixin } from '@/mixins/error-handler.mixin'
import { mapGetters } from 'vuex'

export default {
  mixins: [ ErrorHandlerMixin ],
  data() {
    return {
      loading: false,
      tillages: [],
      previousCrops: [],
    }
  },
  props:{
    isNextStepAvailable: {},
  },
  methods: {

    save() {
      this.loading = true;
      this.$store.dispatch('program/general-settings/save')
        .then(() => {
          // this.$notify({
          //   group: 'notices',
          //   title: this.$ml.get(`Step was successfully updated!`),
          //   type: 'success',
          // });
          this.$store.dispatch('program/canGoToNextStep', true);
        })
        .catch(this.handleErrorAsNotifications)
        .finally(() => { this.loading = false; });
    },

    selectTillage() {
      return API.misc.tillage().then(res => {
        this.tillages = res.data.data;
      });
    },

    selectPrevCrops() {
      return ProgramAPI.previousCrops().then(res => {
        this.previousCrops = res.data.data;
      });
    },
  },
  watch: {
    isSoilTest(to, from) {
      if(this.form.previousCropId) {
        const crop = this.previousCrops.find(x => x.id == this.form.previousCropId)
        if(crop) {
          this.form.previousCropId = this.previousCrops.find(x => x.name == crop.name && x.soilTest == to).id;
        }
      } else {
        this.form.previousCropId = null;
      }
    },
    // 'form.mediaAnalysis': function(val) {
    //   if(!val && this.program.type == 'hydroponic') this.form.tissueTest = false;
    // },
    // 'form.tissueTest': function(val) {
    //   if(val && this.program.type == 'hydroponic') this.form.mediaAnalysis = true;
    // }
  },
  computed: {
    ...mapGetters({
      program: 'program/data',
      form: 'program/general-settings/form',
      farms: 'farms/farms',
      getFarmById: 'farms/getFarmById',
      isFinished: 'program/isFinished',
    }),
    fields() {
      if(this.form.farmId) {
        return this.getFarmById(this.form.farmId).fields;
      }
    },
    isSoilTest() {
      return !!this.form.soilTest;
    },
    // tissueTestCheckbox: {
    //   get() {
    //     return this.form.tissueTest;
    //   },
    //   set(val) {
    //     this.form.tissueTest = val;
    //     if(val && this.program.type == 'hydroponic') {
    //       this.$notify({
    //         group: 'notices',
    //         title: this.$ml.get(`NOTE: Tissue test works only in combination with Media Analysis.`),
    //         type : 'warning',
    //       });
    //     }
    //   }
    // }
  },

  created() {
    this.$store.dispatch('program/general-settings/initData');
    this.selectTillage();
    this.selectPrevCrops();
  },

  components: {
    CropSelection,
  }

}
</script>

<style lang="scss" scoped>

.general-settings {

  .heading {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .a-rows {
    margin-right: -16px;
    margin-left: -16px;
    width: auto!important;
    &::v-deep {
      .a-row {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: flex-start;
        // margin-bottom: 32px;
        .a-column {
          flex-basis: calc(33% - 32px);
          margin: 16px 16px 32px 16px;
          .text-input {
            margin-bottom: 0;
          }
        }
        .check{
          margin-bottom: 0px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .a-rows::v-deep {

      .a-row {
        flex-direction: column;
        margin-bottom: 0;
        .a-column {
          margin-bottom: 24px;
        }
        .check{
          margin-bottom: 0px;
        }
      }
    }
  }
}
</style>
